/**
 * The inline "add your key" card shown where a BYOK-locked feature would be.
 *
 * It sits exactly where the feature would have rendered — which is where the
 * user is already looking — rather than as a banner, a modal, or a redirect.
 * It is a door, not a wall: small, non-blocking, and it never takes the page
 * away from the user.
 *
 * Three states, driven by the user's saved keys (`useByokGateState`):
 *
 * - `loading` — a card-shaped skeleton, never empty space
 * - `none`    — no capable provider has a key: "Add your key"
 * - `failing` — a key exists but its last check failed: "Your OpenAI key isn't
 *   working", with "Re-check key" and "Replace key". The provider's own error
 *   text is never rendered (it can echo the key); only a fixed localized
 *   reason and the last-checked date are.
 *
 * Copy discipline: this card renders on deployments where payments are off, so
 * it must contain no money vocabulary at all — no currency symbols, no
 * "credits", no processor names. The paywall-off DOM sweeps
 * (`frontend/src/__tests__/paywall.test.tsx`,
 * `frontend/e2e/health-assistant/paywall-off.spec.ts`) fail the build if that
 * slips.
 *
 * @module _shared_frontend/components/paywall/ByokRequiredCta
 */

import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { AudioLines, Bot, KeyRound, RefreshCw, Sparkles, TriangleAlert } from "lucide-react";
import { localizedPath } from "@shared/lib/localized-path";
import {
	AI_FEATURE_RETURN_TARGETS,
	AI_KEYS_PATH,
	aiKeysDeepLinkPath,
} from "@shared/lib/paywall";
import { useDocumentTitleState } from "@shared/seo/documentTitleState";
import { Skeleton } from "@shared/ui/skeleton";
import { cn } from "@shared/lib/utils";
import type { ByokProvider } from "@apps/ai_services/frontend/api";
import { useByokGateState } from "@apps/ai_services/frontend/hooks/use-byok-gate-state";
import { useTestByokKey } from "@apps/ai_services/frontend/hooks/use-byok";
import { providerName } from "@apps/ai_services/frontend/components/ByokKeyManager/providerMeta";

/** The key manager page (`apps/ai_services/urls.py` → `frontend_patterns`). */
export { AI_KEYS_PATH };

export interface ByokRequiredCtaProps {
	/** The AI feature the user was trying to reach, e.g. `"health_assistant"`. */
	featureKey: string;
	className?: string;
}

/** Generic glyphs, not brand logos: no licensing, no drift from real marks. */
const PROVIDER_ICONS: Record<string, typeof Sparkles> = {
	assemblyai: AudioLines,
	openai: Sparkles,
};

/**
 * Both classes carry a border that is thicker than the default and a
 * start-edge accent, so the card is distinguishable without relying on colour.
 * Motion is a one-shot 200ms entrance; `motion-reduce` removes it.
 */
const CARD_CLASSES =
	"rounded-lg border-2 border-s-4 border-border border-s-primary bg-muted/40 p-4 text-start " +
	"animate-in fade-in slide-in-from-bottom-1 duration-200 motion-reduce:animate-none " +
	"transition-shadow ease-out hover:shadow-sm motion-reduce:transition-none";

const BUTTON_BASE =
	"inline-flex min-h-[44px] items-center gap-2 rounded-md px-4 py-2 text-sm font-medium " +
	"transition-colors transition-shadow duration-150 ease-out active:scale-[0.98] " +
	"motion-reduce:transition-none motion-reduce:active:scale-100 " +
	"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background";
const BUTTON_PRIMARY = `${BUTTON_BASE} bg-primary text-primary-foreground hover:bg-primary/90 hover:shadow-sm`;
const BUTTON_OUTLINE = `${BUTTON_BASE} border border-input hover:bg-accent hover:text-accent-foreground`;

function useListFormatter(language: string) {
	return (items: string[]): string => {
		try {
			return new Intl.ListFormat(language, {
				style: "long",
				type: "disjunction",
			}).format(items);
		} catch {
			return items.join(", ");
		}
	};
}

function formatCheckedAt(iso: string, language: string): string | null {
	const date = new Date(iso);
	if (Number.isNaN(date.getTime())) return null;
	try {
		return new Intl.DateTimeFormat(language, {
			dateStyle: "medium",
			timeStyle: "short",
		}).format(date);
	} catch {
		return date.toLocaleString();
	}
}

/**
 * "Re-check key". Its own component so the mutation hooks (which need a
 * `QueryClientProvider`) mount only in the failing state, and so the card
 * announces a result without owning the request.
 */
function RecheckButton({
	provider,
	onStatus,
}: {
	provider: ByokProvider;
	onStatus: (message: string) => void;
}) {
	const { t } = useTranslation("byok");
	const recheck = useTestByokKey();
	const handleRecheck = () => {
		onStatus(t("cta.rechecking", "Re-checking your key…"));
		recheck.mutate(provider, {
			onSuccess: (row) =>
				onStatus(
					row.last_test_status === "failed"
						? t("cta.recheckFailed", "Still not working. Replace the key to continue.")
						: t("cta.recheckOk", "Key re-checked and working."),
				),
			onError: () =>
				onStatus(t("cta.recheckError", "Could not re-check the key. Try again.")),
		});
	};
	return (
		<button
			type="button"
			className={BUTTON_PRIMARY}
			onClick={handleRecheck}
			disabled={recheck.isPending}
			data-testid="byok-cta-recheck"
		>
			<RefreshCw
				className={cn("h-4 w-4", recheck.isPending && "animate-spin motion-reduce:animate-none")}
				aria-hidden="true"
			/>
			{t("cta.recheck", "Re-check key")}
		</button>
	);
}

export function ByokRequiredCta({ featureKey, className }: ByokRequiredCtaProps) {
	const { t, i18n } = useTranslation("byok");
	const { t: tCommon } = useTranslation("common");
	const titleId = useId();
	const gate = useByokGateState(featureKey);
	// The tab title carries the state ("Voice Log - key needed | …"); it clears
	// itself when the card unmounts, i.e. as soon as the feature unlocks.
	useDocumentTitleState(
		gate.kind === "failing"
			? tCommon("meta.states.keyFailing")
			: gate.kind === "none"
				? tCommon("meta.states.keyNeeded")
				: null,
	);
	const [statusMessage, setStatusMessage] = useState("");
	const language = i18n.language || "en";
	const formatList = useListFormatter(language);

	if (gate.kind === "loading") {
		return (
			<div
				className={cn(CARD_CLASSES, className)}
				role="status"
				aria-busy="true"
				data-testid="byok-required-cta-loading"
				data-feature-key={featureKey}
			>
				<span className="sr-only">{t("cta.loading", "Checking your keys…")}</span>
				<Skeleton className="h-5 w-2/3" />
				<Skeleton className="mt-3 h-4 w-full" />
				<Skeleton className="mt-2 h-4 w-5/6" />
				<Skeleton className="mt-4 h-11 w-36" />
			</div>
		);
	}

	const failing = gate.kind === "failing";
	const targetProvider = failing ? gate.provider : gate.providers[0];
	const href = localizedPath(
		targetProvider ? aiKeysDeepLinkPath(targetProvider, featureKey) : AI_KEYS_PATH,
	);
	const names = gate.providers.map((provider) => providerName(provider));
	const returnTarget = AI_FEATURE_RETURN_TARGETS[featureKey];
	const featureName = returnTarget
		? t(returnTarget.nameKey, "This feature")
		: t(`features.${featureKey}`, "This feature");
	const checkedAt =
		failing && gate.lastTestedAt ? formatCheckedAt(gate.lastTestedAt, language) : null;

	return (
		<section
			role="region"
			aria-labelledby={titleId}
			className={cn(CARD_CLASSES, className)}
			data-testid="byok-required-cta"
			data-state={gate.kind}
			data-feature-key={featureKey}
		>
			{/* `dir="rtl"` already flips a flex row on its own; adding a reverse
			    utility here would flip it back and put the icon on the wrong
			    side. Logical properties only. */}
			<div className="flex items-center gap-2">
				{failing ? (
					<TriangleAlert className="h-5 w-5 shrink-0" aria-hidden="true" />
				) : (
					<KeyRound className="h-5 w-5 shrink-0" aria-hidden="true" />
				)}
				<h2 id={titleId} className="text-base font-semibold">
					{failing
						? t("cta.failingTitle", {
								defaultValue: "Your {{provider}} key isn't working",
								provider: providerName(gate.provider),
							})
						: t("cta.title", "This one runs on your own key")}
				</h2>
			</div>

			<p className="mt-2 text-sm text-muted-foreground">
				{failing
					? t("cta.failingBody", {
							defaultValue: "{{feature}} needs a working key. Re-check it or replace it.",
							feature: featureName,
						})
					: t(
							"cta.body",
							"Add your own provider key to switch this on. The app stays free — you pay your provider directly.",
						)}
			</p>

			{failing ? (
				<div className="mt-2 space-y-1 text-sm text-muted-foreground" data-testid="byok-cta-failure-detail">
					<p>
						{t(
							"cta.failingReason",
							"The provider rejected this key on the last check.",
						)}
					</p>
					{checkedAt ? (
						<p data-testid="byok-cta-last-checked">
							{t("cta.lastChecked", {
								defaultValue: "Last checked {{date}}",
								date: checkedAt,
							})}
						</p>
					) : null}
				</div>
			) : null}

			{names.length > 0 ? (
				<div className="mt-3" data-testid="byok-cta-providers">
					<p className="text-sm font-medium text-foreground">
						{t("cta.providers", {
							defaultValue: "Works with: {{list}}",
							list: formatList(names),
						})}
					</p>
					<ul className="mt-2 space-y-1.5 text-sm text-muted-foreground">
						{gate.providers.map((provider) => {
							const Icon = PROVIDER_ICONS[provider] ?? Bot;
							return (
								<li
									key={provider}
									className="flex items-center gap-2"
									data-testid={`byok-cta-provider-${provider}`}
								>
									<Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
									<span>
										<span className="font-medium text-foreground">
											{providerName(provider)}
										</span>
										{": "}
										{t(
											`cta.providerRole.${featureKey}.${provider}`,
											t("cta.providerRoleDefault", "powers this feature"),
										)}
									</span>
								</li>
							);
						})}
					</ul>
				</div>
			) : null}

			<div className="mt-4 flex flex-wrap items-center gap-3">
				{failing ? (
					<>
						<RecheckButton provider={gate.provider} onStatus={setStatusMessage} />
						<a href={href} className={BUTTON_OUTLINE} data-testid="byok-cta-link">
							<KeyRound className="h-4 w-4" aria-hidden="true" />
							{t("cta.replace", "Replace key")}
						</a>
					</>
				) : (
					<a href={href} className={BUTTON_PRIMARY} data-testid="byok-cta-link">
						<KeyRound className="h-4 w-4" aria-hidden="true" />
						{t("cta.action", "Add your key")}
					</a>
				)}
			</div>

			{/* Always mounted so assistive tech registers the region before its
			    text changes. */}
			<p
				role="status"
				aria-live="polite"
				className="mt-3 min-h-[1.25rem] text-sm text-foreground"
				data-testid="byok-cta-status"
			>
				{statusMessage}
			</p>
		</section>
	);
}

export default ByokRequiredCta;
